"use client"
import React, { useState, FormEvent, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";

const ContactForm: React.FC = () => {
  const recaptchaRef = React.createRef<any>();

  const [formState, setFormState] = useState({
    fname: "",
    lname: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);

  const handleRecaptchaChange = (token: string | null) => {
    console.log('ReCAPTCHA changed:', token);
    setRecaptchaCompleted(!!token);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    console.log('Form submission - recaptchaCompleted:', recaptchaCompleted);

    if (!recaptchaCompleted) {
      console.log('ReCAPTCHA not completed, showing error');
      setError("Please check the reCAPTCHA box before submitting.");
      return;
    }

    const response = await fetch("/api/contact", {
      method: "POST",
      body: JSON.stringify(formState),
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    console.log(data);

    if (response.ok) {
      setSubmitted(true);
      setError(null); // Clear any previous errors
    } else {
      setError(data.message || "An error occurred"); // Update error state with error message
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-transparent p-8 border border-border rounded-2xl w-full max-w-lg">
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2 px-3 my-4 md:mb-0">
          <input
            className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 leading-tight focus:outline-none placeholder:text-text-muted"
            id="grid-first-name"
            type="text"
            name="fname"
            placeholder="First Name"
            disabled={isSubmitted}
            required
            value={formState.fname}
            onChange={handleInputChange}
          />
        </div>
        <div className="w-full md:w-1/2 px-3 my-4 md:mb-0">
          <input
            className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 leading-tight focus:outline-none placeholder:text-text-muted"
            id="grid-last-name"
            type="text"
            name="lname"
            placeholder="Last Name"
            disabled={isSubmitted}
            required
            value={formState.lname}
            onChange={handleInputChange}
          />
        </div>
        <div className="w-full md:w-1/2 px-3 my-4 md:mb-0">
          <input
            className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 leading-tight focus:outline-none placeholder:text-text-muted"
            id="grid-email-name"
            type="email"
            name="email"
            placeholder="Email"
            disabled={isSubmitted}
            required
            value={formState.email}
            onChange={handleInputChange}
          />
        </div>
        <div className="w-full md:w-1/2 px-3 my-4">
          <input
            className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 leading-tight focus:outline-none placeholder:text-text-muted"
            id="grid-phone-number"
            type="tel"
            name="phone"
            placeholder="Phone Number"
            disabled={isSubmitted}
            required
            value={formState.phone}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="w-full px-3 mb-4">
        <input
          className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 leading-tight focus:outline-none placeholder:text-text-muted"
          id="grid-subject"
          type="text"
          name="subject"
          placeholder="Subject"
          disabled={isSubmitted}
          required
          value={formState.subject}
          onChange={handleInputChange}
        />
      </div>
      <div className="flex flex-wrap mb-3">
        <div className="w-full px-3">
          <textarea
            className="appearance-none block w-full bg-transparent text-text border-b border-border py-3 px-4 mb-3 leading-tight focus:outline-none h-48 placeholder:text-text-muted"
            id="grid-message"
            name="message"
            placeholder="What's on your mind?"
            disabled={isSubmitted}
            required
            value={formState.message}
            onChange={handleInputChange}
          ></textarea>
        </div>
      </div>
      {isSubmitted && (
        <AnimatePresence mode="wait">
          <motion.div
            className="bg-green-300 text-green-900 p-5 rounded-lg mb-6 text-center"
            key="success-alert"
            initial={{ opacity: 0, scale: 0.5, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -50 }}
            transition={{
              duration: 1.5,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            Your message was submitted successfully!
          </motion.div>
        </AnimatePresence>
      )}
      {error && (
        <AnimatePresence mode="wait">
          <motion.div
            className="bg-red-300 text-red-900 p-5 rounded-lg mb-6
              text-center"
            key="success-alert"
            initial={{ opacity: 0, scale: 0.5, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -50 }}
            transition={{
              duration: 1.5,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            Error: {error}
          </motion.div>
        </AnimatePresence>
      )}
      <div className="flex-col md:flex md:items-center">
        <div className="w-full flex flex-wrap content-center items-center justify-center mb-4">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6LcSbHwmAAAAAG-xhiMHR1NerHt0iINzcPa1l36z"
            onChange={handleRecaptchaChange}
          />
        </div>

        <div className="w-full flex content-center items-center justify-center">
          <Button
            variant="default"
            type="submit"
            disabled={isSubmitted}
            data-callback="onSubmit"
            data-action="submit"
            className="g-recaptcha"
          >
            Send Message
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
